import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ChevronDown, 
  ChevronRight, 
  Download, 
  ArrowLeft,
  Search,
  X,
  MessageSquare,
  Award,
  Paperclip,
  FileText
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country configuration with certifications - NO Portugal or Luxembourg
const COUNTRY_CONFIG = {
  ES: {
    code: 'ES',
    name: { en: 'Spain', es: 'España', fr: 'Espagne', de: 'Spanien', nl: 'Spanje' },
    certification: 'AW',
    mapImage: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9a/EU-Spain.svg/1200px-EU-Spain.svg.png',
    plant: 'sevilla',
  },
  BE: {
    code: 'BE',
    name: { en: 'Belgium', es: 'Bélgica', fr: 'Belgique', de: 'Belgien', nl: 'België' },
    certification: 'BENOR',
    mapImage: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/EU-Belgium.svg/1200px-EU-Belgium.svg.png',
    plant: 'liege',
  },
  NL: {
    code: 'NL',
    name: { en: 'Netherlands', es: 'Países Bajos', fr: 'Pays-Bas', de: 'Niederlande', nl: 'Nederland' },
    certification: 'KIWA',
    mapImage: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/EU-Netherlands.svg/1200px-EU-Netherlands.svg.png',
    plant: 'liege',
  },
  DE: {
    code: 'DE',
    name: { en: 'Germany', es: 'Alemania', fr: 'Allemagne', de: 'Deutschland', nl: 'Duitsland' },
    certification: 'MPA',
    mapImage: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e6/EU-Germany.svg/1200px-EU-Germany.svg.png',
    plant: 'liege',
  },
  FR: {
    code: 'FR',
    name: { en: 'France', es: 'Francia', fr: 'France', de: 'Frankreich', nl: 'Frankrijk' },
    certification: 'AFCAB',
    mapImage: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/EU-France.svg/1200px-EU-France.svg.png',
    plant: 'liege',
  },
};

// Order of countries to display
const COUNTRY_ORDER = ['ES', 'BE', 'NL', 'DE', 'FR'];

// ============== TRH LIÈGE PRODUCTION (Belgium Plant) ==============
const MESH_FAMILIES_LIEGE = [
  {
    name: 'Q Mesh',
    markets: ['DE'],
    products: [
      { ref: 'Q188-A', diameter: '6-6', dimensions: '6000 × 2300', trama: '150-150', weightPiece: 41.70, piecesBundle: 50, weightBundle: 2085, markets: ['DE'], datasheet: '/api/datasheets/g5zkh99e_1.png' },
      { ref: 'Q257-A', diameter: '7-7', dimensions: '6000 × 2300', trama: '150-150', weightPiece: 56.80, piecesBundle: 40, weightBundle: 2272, markets: ['DE'], datasheet: '/api/datasheets/ooz4ejd0_2.png' },
      { ref: 'Q335-A', diameter: '8-8', dimensions: '6000 × 2300', trama: '150-150', weightPiece: 74.30, piecesBundle: 30, weightBundle: 2229, markets: ['DE'], datasheet: '/api/datasheets/tz78tpbi_3.png' },
      { ref: 'Q424-A', diameter: '9-9', dimensions: '6000 × 2300', trama: '150-150', weightPiece: 84.40, piecesBundle: 40, weightBundle: 2532, markets: ['DE'], datasheet: '/api/datasheets/agqwss2y_4.png' },
      { ref: 'Q524-A', diameter: '10-10', dimensions: '6000 × 2300', trama: '150-150', weightPiece: 100.90, piecesBundle: 20, weightBundle: 2018, markets: ['DE'], datasheet: '/api/datasheets/h0rucups_5.png' },
    ]
  },
  {
    name: 'R Mesh',
    markets: ['DE'],
    products: [
      { ref: 'R188-A', diameter: '6-6', dimensions: '6000 × 2300', trama: '150-250', weightPiece: 33.60, piecesBundle: 50, weightBundle: 1680, markets: ['DE'], datasheet: '/api/datasheets/qviw85oi_1.png' },
      { ref: 'R257-A', diameter: '7-7', dimensions: '6000 × 2300', trama: '150-250', weightPiece: 41.20, piecesBundle: 50, weightBundle: 2060, markets: ['DE'], datasheet: '/api/datasheets/ep6fgff1_2.png' },
      { ref: 'R335-A', diameter: '8-6', dimensions: '6000 × 2300', trama: '150-250', weightPiece: 50.20, piecesBundle: 40, weightBundle: 2008, markets: ['DE'], datasheet: '/api/datasheets/fp1smyjp_3.png' },
      { ref: 'R524-A', diameter: '10-8', dimensions: '6000 × 2300', trama: '150-250', weightPiece: 75.70, piecesBundle: 30, weightBundle: 2271, markets: ['DE'], datasheet: '/api/datasheets/2bbzkuxd_4.png' },
    ]
  },
  {
    name: 'BEC Mesh',
    markets: ['NL'],
    products: [
      { ref: 'BEC-188A', diameter: '6-6', dimensions: '5950 × 2350', trama: '150-150', weightPiece: 36.47, piecesBundle: 40, weightBundle: 1458, markets: ['NL'], datasheet: '/api/datasheets/8oifhheg_1.png' },
      { ref: 'BEC-257A', diameter: '7-7', dimensions: '5960 × 2360', trama: '150-150', weightPiece: 49.77, piecesBundle: 30, weightBundle: 1493, markets: ['NL'], datasheet: '/api/datasheets/jtnwe4kr_2.png' },
      { ref: 'BEC-335A', diameter: '8-8', dimensions: '4450 × 2350', trama: '150-150', weightPiece: 45.23, piecesBundle: 30, weightBundle: 1356, markets: ['NL'], datasheet: '/api/datasheets/9cukndmu_3.png' },
      { ref: 'BEC-503A', diameter: '8-8', dimensions: '3500 × 2200', trama: '100-100', weightPiece: 49.57, piecesBundle: 30, weightBundle: 1487, markets: ['NL'], datasheet: '/api/datasheets/ixqb7p76_4.png' },
      { ref: 'BEC-524A', diameter: '10-10', dimensions: '5900 × 2300', trama: '150-150', weightPiece: 89.71, piecesBundle: 15, weightBundle: 1345, markets: ['NL'], datasheet: '/api/datasheets/g588je06_5.png' },
    ]
  },
  {
    name: 'KL Mesh',
    markets: ['NL'],
    products: [
      { ref: 'KL-189A', diameter: '6-6', dimensions: '3700 × 2350', trama: '150-150', weightPiece: 22.16, piecesBundle: 50, weightBundle: 1107, markets: ['NL'], datasheet: '/api/datasheets/4p98fvcj_1.png' },
      { ref: 'KL-257A', diameter: '7-7', dimensions: '3650 × 2300', trama: '150-150', weightPiece: 29.84, piecesBundle: 50, weightBundle: 1491, markets: ['NL'], datasheet: '/api/datasheets/ina5lnqz_2.png' },
    ]
  },
  {
    name: 'B Mesh',
    markets: ['NL', 'BE'],
    products: [
      { ref: 'B-188A', diameter: '6-6', dimensions: '5950 × 2350', trama: '150-150', weightPiece: 38.32, piecesBundle: 40, weightBundle: 1532, markets: ['NL', 'BE'], datasheet: '/api/datasheets/d8ty2cf1_1.png' },
      { ref: 'B-257A', diameter: '7-7', dimensions: '5950 × 2350', trama: '150-150', weightPiece: 52.13, piecesBundle: 30, weightBundle: 1563, markets: ['NL', 'BE'], datasheet: '/api/datasheets/6snlmdyp_2.png' },
      { ref: 'B-335A', diameter: '8-8', dimensions: '5950 × 2350', trama: '150-150', weightPiece: 64.90, piecesBundle: 20, weightBundle: 1297, markets: ['NL', 'BE'], datasheet: '/api/datasheets/wqrjovra_3.png' },
      { ref: 'B-424A', diameter: '9-9', dimensions: '5950 × 2350', trama: '150-150', weightPiece: 81.99, piecesBundle: 20, weightBundle: 1639, markets: ['NL', 'BE'], datasheet: '/api/datasheets/58x4mj8v_4.png' },
      { ref: 'B-503A', diameter: '8-8', dimensions: '6000 × 2400', trama: '100-100', weightPiece: 100.49, piecesBundle: 10, weightBundle: 1004, markets: ['NL', 'BE'], datasheet: '/api/datasheets/vg1ww9r3_5.png' },
      { ref: 'B-524A', diameter: '10-10', dimensions: '5950 × 2350', trama: '150-150', weightPiece: 101.37, piecesBundle: 10, weightBundle: 1013, markets: ['NL', 'BE'], datasheet: '/api/datasheets/tsvxu79c_6.png' },
      { ref: 'B-636A', diameter: '9-9', dimensions: '6000 × 2400', trama: '100-100', weightPiece: 126.95, piecesBundle: 10, weightBundle: 1269, markets: ['NL', 'BE'], datasheet: '/api/datasheets/3snmx434_7.png' },
      { ref: 'B-754A', diameter: '12-12', dimensions: '5950 × 2350', trama: '150-150', weightPiece: 138.53, piecesBundle: 10, weightBundle: 1385, markets: ['NL', 'BE'], datasheet: '/api/datasheets/dveqg9t5_8.png' },
      { ref: 'B-785A', diameter: '10-10', dimensions: '6000 × 2400', trama: '100-100', weightPiece: 156.97, piecesBundle: 10, weightBundle: 1569, markets: ['NL', 'BE'], datasheet: '/api/datasheets/22o02obq_9.png' },
    ]
  },
  {
    name: 'P Mesh',
    markets: ['NL', 'BE'],
    products: [
      { ref: 'P-131A', diameter: '5-5', dimensions: '5000 × 2000', trama: '150-150', weightPiece: 20.19, piecesBundle: 50, weightBundle: 1009, markets: ['NL', 'BE'], datasheet: '/api/datasheets/2ask7m0v_1.png' },
      { ref: 'P-189A', diameter: '6-6', dimensions: '5000 × 2000', trama: '150-150', weightPiece: 29.08, piecesBundle: 50, weightBundle: 1454, markets: ['NL', 'BE'], datasheet: '/api/datasheets/czl8bgnr_2.png' },
      { ref: 'P-335A', diameter: '8-8', dimensions: '5000 × 2000', trama: '150-150', weightPiece: 51.75, piecesBundle: 30, weightBundle: 1552, markets: ['NL', 'BE'], datasheet: '/api/datasheets/qq2kqzdv_3.png' },
      { ref: 'P-524A', diameter: '10-10', dimensions: '5000 × 2000', trama: '150-150', weightPiece: 80.83, piecesBundle: 20, weightBundle: 1616, markets: ['NL', 'BE'], datasheet: '/api/datasheets/sii66gus_4.png' },
      { ref: 'P-754A', diameter: '12-12', dimensions: '5000 × 2000', trama: '150-150', weightPiece: 116.33, piecesBundle: 10, weightBundle: 1163, markets: ['NL', 'BE'], datasheet: '/api/datasheets/o2fsorc4_5.png' },
    ]
  },
  {
    name: 'PS Mesh',
    markets: ['NL', 'BE'],
    products: [
      { ref: 'PS-130A', diameter: '5-5', dimensions: '3000 × 2000', trama: '150-150', weightPiece: 12.64, piecesBundle: 50, weightBundle: 632, markets: ['NL', 'BE'], datasheet: '/api/datasheets/ql10r2y0_1.png' },
      { ref: 'PS-188A', diameter: '6-6', dimensions: '3000 × 2000', trama: '150-150', weightPiece: 18.20, piecesBundle: 50, weightBundle: 910, markets: ['NL', 'BE'], datasheet: '/api/datasheets/e94gg1vy_2.png' },
      { ref: 'PS-335A', diameter: '8-8', dimensions: '3000 × 2000', trama: '150-150', weightPiece: 32.39, piecesBundle: 40, weightBundle: 1295, markets: ['NL', 'BE'], datasheet: '/api/datasheets/6e32wdo3_3.png' },
      { ref: 'PS-524A', diameter: '10-10', dimensions: '3000 × 2000', trama: '150-150', weightPiece: 50.59, piecesBundle: 30, weightBundle: 1517, markets: ['NL', 'BE'], datasheet: '/api/datasheets/wuq1myii_4.png' },
    ]
  },
  {
    name: 'TRHST Mesh',
    markets: ['FR'],
    products: [
      { ref: 'TRHST-15C', diameter: '6-6', dimensions: '4000 × 2400', trama: '100-100', weightPiece: 21.31, piecesBundle: 70, weightBundle: 1491, markets: ['FR'], datasheet: '/api/datasheets/0zkeydk7_9.pdf' },
      { ref: 'TRHST-20', diameter: '6-7', dimensions: '6000 × 2400', trama: '150-300', weightPiece: 35.81, piecesBundle: 40, weightBundle: 1432, markets: ['FR'], datasheet: '/api/datasheets/vw9dagla_2.png' },
      { ref: 'TRHST-25', diameter: '7-7', dimensions: '6000 × 2400', trama: '150-300', weightPiece: 43.49, piecesBundle: 40, weightBundle: 1739, markets: ['FR'], datasheet: '/api/datasheets/omxjxz9p_3.png' },
      { ref: 'TRHST-25C', diameter: '7-7', dimensions: '6000 × 2400', trama: '150-150', weightPiece: 57.98, piecesBundle: 30, weightBundle: 1739, markets: ['FR'], datasheet: '/api/datasheets/pgi6j00n_4.png' },
      { ref: 'TRHST-25CS', diameter: '7-7', dimensions: '3000 × 2400', trama: '150-150', weightPiece: 28.99, piecesBundle: 40, weightBundle: 1159, markets: ['FR'], datasheet: '/api/datasheets/9ecbyxbe_5.png' },
      { ref: 'TRHST-35', diameter: '7-7', dimensions: '6000 × 2400', trama: '100-300', weightPiece: 57.98, piecesBundle: 30, weightBundle: 1739, markets: ['FR'], datasheet: '/api/datasheets/ov3visqs_6.png' },
      { ref: 'TRHST-50', diameter: '8-8', dimensions: '6000 × 2400', trama: '100-300', weightPiece: 75.84, piecesBundle: 20, weightBundle: 1516, markets: ['FR'], datasheet: '/api/datasheets/5ikc5rav_Beelgica-TRHST_Familia-Planilla_TRHSteel.png' },
      { ref: 'TRHST-50C', diameter: '8-8', dimensions: '6000 × 2400', trama: '100-100', weightPiece: 113.76, piecesBundle: 15, weightBundle: 1706, markets: ['FR'], datasheet: '/api/datasheets/rq1z26zc_7.png' },
      { ref: 'TRHST-60', diameter: '9-9', dimensions: '6000 × 2400', trama: '100-250', weightPiece: 100.60, piecesBundle: 16, weightBundle: 1609, markets: ['FR'], datasheet: '/api/datasheets/8ctbjukk_8.png' },
      { ref: 'TRHST-65C', diameter: '9-9', dimensions: '6000 × 2400', trama: '100-100', weightPiece: 143.71, piecesBundle: 10, weightBundle: 1437, markets: ['FR'], datasheet: '/api/datasheets/mto0abtp_9.png' },
    ]
  }
];

// ============== TRH SEVILLA PRODUCTION (Spain Plant) ==============
const MESH_FAMILIES_SEVILLA = [
  {
    name: 'Q-Ahorro Simple',
    markets: ['ES'],
    products: [
      { ref: 'C1504 Q', diameter: '4-4', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 15.18, piecesBundle: 100, weightBundle: 1518, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/mrsy6nwj_1.png` },
      { ref: 'C1505 Q', diameter: '5-5', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 24.65, piecesBundle: 70, weightBundle: 1725, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/jj1ucw5f_4.png` },
      { ref: 'C1506 Q', diameter: '6-6', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 35.52, piecesBundle: 50, weightBundle: 1776, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/39ysc27s_8.png` },
      { ref: 'C1508 Q', diameter: '8-8', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 60.83, piecesBundle: 30, weightBundle: 1824, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/7k4mqve5_11.png` },
      { ref: 'C1510 Q', diameter: '10-10', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 95.01, piecesBundle: 20, weightBundle: 1900, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/v4322t0y_13.png` },
      { ref: 'C1512 Q', diameter: '12-12', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 126.09, piecesBundle: 15, weightBundle: 1891, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/2vf6t9mu_15.png` },
      { ref: 'C2004 Q', diameter: '4-4', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 11.83, piecesBundle: 100, weightBundle: 1183, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/sko3an18_2.png` },
      { ref: 'C2005 Q', diameter: '5-5', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 18.49, piecesBundle: 90, weightBundle: 1664, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/bw0zipav_5.png` },
      { ref: 'C2006 Q', diameter: '6-6', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 26.64, piecesBundle: 60, weightBundle: 1598, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/od7dhcu2_9.png` },
      { ref: 'C2008 Q', diameter: '8-8', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 45.03, piecesBundle: 40, weightBundle: 1801, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/4wx26feh_12.png` },
      { ref: 'C2010 Q', diameter: '10-10', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 70.33, piecesBundle: 25, weightBundle: 1758, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/l23pyx61_14.png` },
      { ref: 'R1505 Q', diameter: '5-5', dimensions: '6000 × 2200', trama: '150-300', weightPiece: 17.87, piecesBundle: 90, weightBundle: 1608, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/cn37kblm_6.png` },
      { ref: 'R1506 Q', diameter: '6-6', dimensions: '6000 × 2200', trama: '150-300', weightPiece: 25.75, piecesBundle: 60, weightBundle: 1545, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/xyvsrqr4_10.png` },
      { ref: 'R2004 Q', diameter: '4-4', dimensions: '6000 × 2200', trama: '200-300', weightPiece: 9.66, piecesBundle: 110, weightBundle: 1062, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/4xake42v_3.png` },
      { ref: 'R2005 Q', diameter: '5-5', dimensions: '6000 × 2200', trama: '200-300', weightPiece: 15.10, piecesBundle: 90, weightBundle: 1062, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/gf93a3iw_7.png` },
      { ref: 'C2012 Q', diameter: '12-12', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 95.94, piecesBundle: 20, weightBundle: 1918, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/1oneifmo_16.png` },
    ]
  },
  {
    name: 'ECO',
    markets: ['ES'],
    products: [
      { ref: 'C1504 ECO', diameter: '4-4', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 14.53, piecesBundle: 110, weightBundle: 1598, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/ir19qypi_1.png` },
      { ref: 'C1505 ECO', diameter: '5-5', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 22.71, piecesBundle: 70, weightBundle: 1590, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/3wmub2d9_3.png` },
      { ref: 'C1506 ECO', diameter: '6-6', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 32.72, piecesBundle: 50, weightBundle: 1636, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/p2m0qp96_5.png` },
      { ref: 'C1508 ECO', diameter: '8-8', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 58.22, piecesBundle: 30, weightBundle: 1746, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/63r2xi7e_7.png` },
      { ref: 'C1510 ECO', diameter: '10-10', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 85.88, piecesBundle: 20, weightBundle: 1717, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/5p0yjohc_9.png` },
      { ref: 'C1512 ECO', diameter: '12-12', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 123.61, piecesBundle: 15, weightBundle: 1854, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/nziz2fqz_11.png` },
      { ref: 'C2004 ECO', diameter: '4-4', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 11.18, piecesBundle: 110, weightBundle: 1229, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/fznu2evj_2.png` },
      { ref: 'C2005 ECO', diameter: '5-5', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 17.47, piecesBundle: 90, weightBundle: 1572, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/njpxkclv_4.png` },
      { ref: 'C2006 ECO', diameter: '6-6', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 25.17, piecesBundle: 60, weightBundle: 1510, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/q172y435_6.png` },
      { ref: 'C2008 ECO', diameter: '8-8', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 42.42, piecesBundle: 40, weightBundle: 1696, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/oa85wrk5_8.png` },
      { ref: 'C2010 ECO', diameter: '10-10', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 66.26, piecesBundle: 25, weightBundle: 1656, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/k7h0ztu9_10.png` },
      { ref: 'C2012 ECO', diameter: '12-12', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 95.37, piecesBundle: 20, weightBundle: 1907, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/idne01le_12.png` },
    ]
  },
  {
    name: 'ECO +',
    markets: ['ES'],
    products: [
      { ref: 'C1505 ECO+', diameter: '5-5', dimensions: '6000 × 2200', trama: '150-160', weightPiece: 22.03, piecesBundle: 70, weightBundle: 1542, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/0uq37fk6_1.png` },
      { ref: 'C1506 ECO+', diameter: '6-6', dimensions: '6000 × 2200', trama: '150-160', weightPiece: 31.74, piecesBundle: 50, weightBundle: 1587, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/27skyg9v_3.png` },
      { ref: 'C1508 ECO+', diameter: '8-8', dimensions: '6000 × 2200', trama: '150-160', weightPiece: 56.48, piecesBundle: 30, weightBundle: 1694, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/7m5zhmjx_5.png` },
      { ref: 'C2005 ECO+', diameter: '5-5', dimensions: '6000 × 2200', trama: '200-210', weightPiece: 17.13, piecesBundle: 90, weightBundle: 1542, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/pt0bgdsh_2.png` },
      { ref: 'C2006 ECO+', diameter: '6-6', dimensions: '6000 × 2200', trama: '200-210', weightPiece: 24.68, piecesBundle: 60, weightBundle: 1481, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/hx576uor_4.png` },
    ]
  },
  {
    name: 'Unidireccionales',
    markets: ['ES'],
    products: [
      { ref: 'U1508', diameter: '8-6', dimensions: '6000 × 2300', trama: '150-885', weightPiece: 41.49, piecesBundle: 40, weightBundle: 1659, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/7a22l3vh_1.png` },
      { ref: 'U1510', diameter: '10-8', dimensions: '6000 × 2010', trama: '150-750', weightPiece: 58.17, piecesBundle: 25, weightBundle: 1454, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/yhsjlxgb_3.png` },
      { ref: 'U1512', diameter: '12-8', dimensions: '6000 × 2300', trama: '150-828', weightPiece: 91.60, piecesBundle: 20, weightBundle: 1832, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/f2u5lfhc_5.png` },
      { ref: 'U2008', diameter: '8-6', dimensions: '6000 × 2250', trama: '200-885', weightPiece: 31.93, piecesBundle: 40, weightBundle: 1227, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/bemnqr7j_2.png` },
      { ref: 'U2010', diameter: '10-6', dimensions: '6000 × 2250', trama: '200-850', weightPiece: 47.92, piecesBundle: 30, weightBundle: 1437, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/i9wyllva_4.png` },
      { ref: 'U2012', diameter: '12-12', dimensions: '5950 × 2350', trama: '200-200', weightPiece: 70.15, piecesBundle: 20, weightBundle: 1403, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/bq119l22_6.png` },
    ]
  },
  {
    name: 'Puntas Limpias',
    markets: ['ES'],
    products: [
      { ref: 'PL1508', diameter: '8-8', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 60.59, piecesBundle: 20, weightBundle: 1818, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/y775bo8z_1.png` },
      { ref: 'PL1510', diameter: '10-10', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 89.66, piecesBundle: 15, weightBundle: 1344, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/h305meut_3.png` },
      { ref: 'PL1512', diameter: '12-12', dimensions: '6000 × 2200', trama: '150-150', weightPiece: 123.61, piecesBundle: 15, weightBundle: 1854, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/cjeclv5j_5.png` },
      { ref: 'PL2008', diameter: '8-8', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 45.66, piecesBundle: 40, weightBundle: 1826, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/wmytcqo3_2.png` },
      { ref: 'PL2010', diameter: '10-10', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 66.28, piecesBundle: 25, weightBundle: 1657, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/bqrvy3cv_4.png` },
      { ref: 'PL2012', diameter: '12-12', dimensions: '6000 × 2200', trama: '200-200', weightPiece: 95.32, piecesBundle: 20, weightBundle: 1907, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/9mk9ncs2_6.png` },
    ]
  },
  {
    name: 'Cerramiento',
    markets: ['ES'],
    products: [
      { ref: 'COCHINERA', diameter: '5-5', dimensions: '6000 × 1300', trama: '100-150', weightPiece: 11.18, piecesBundle: 60, weightBundle: 1091, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/ho8kcapt_1.png` },
      { ref: 'GANADERA 150', diameter: '5-5', dimensions: '6000 × 1500', trama: '150-150', weightPiece: 18.49, piecesBundle: 70, weightBundle: 1294, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/flafty6f_2.png` },
      { ref: 'GANADERA 165', diameter: '6-6', dimensions: '6000 × 1650', trama: '150-150', weightPiece: 29.30, piecesBundle: 60, weightBundle: 1758, markets: ['ES'], datasheet: `${BACKEND_URL}/api/datasheets/es/u534iqns_3.png` },
    ]
  }
];

// Translations
const translations = {
  en: {
    pageTitle: 'Welded Mesh',
    pageSubtitle: 'Reinforcing steel mesh for construction',
    backToProducts: 'Back to Products',
    selectCountry: 'Select your market',
    viewFamilies: 'View Families',
    certification: 'Certificate',
    searchPlaceholder: 'Search by reference (e.g., Q257-A, ECO-1504...)',
    noResults: 'No references found',
    clearSearch: 'Clear search',
    references: 'references',
    specialMesh: 'Special Mesh',
    specialMeshDesc: 'Need a custom solution? Contact us for special mesh requirements.',
    requestSpecialMesh: 'Request Special Mesh',
    specialMeshFormTitle: 'Special Mesh Request',
    specialMeshFormSubtitle: 'Fill in your details and attach your drawing for a custom mesh quotation.',
    yourName: 'Your Name',
    yourEmail: 'Your Email',
    company: 'Company',
    requirements: 'Requirements / Specifications',
    attachDrawing: 'Attach your drawing',
    attachDrawingHint: 'PDF, DWG, or image files accepted',
    fileSelected: 'File selected',
    removeFile: 'Remove',
    sendRequest: 'Send Request',
    sending: 'Sending...',
    thankYou: 'Thank you!',
    requestReceived: 'Your request has been received. Our team will contact you shortly.',
    close: 'Close',
    tableHeaders: {
      reference: 'Reference',
      diameter: 'Ø (mm)',
      dimensions: 'Dimensions (mm)',
      trama: 'Spacing (mm)',
      weightPiece: 'kg/piece',
      piecesBundle: 'pcs/bundle',
      weightBundle: 'kg/bundle',
      download: 'Download',
    },
    downloadPdf: 'PDF',
    datasheetPending: 'Pending',
  },
  es: {
    pageTitle: 'Malla Electrosoldada',
    pageSubtitle: 'Malla de acero para construcción',
    backToProducts: 'Volver a Productos',
    selectCountry: 'Selecciona tu mercado',
    viewFamilies: 'Ver Familias',
    certification: 'Certificado',
    searchPlaceholder: 'Buscar por referencia (ej: Q257-A, ECO-1504...)',
    noResults: 'No se encontraron referencias',
    clearSearch: 'Limpiar búsqueda',
    references: 'referencias',
    specialMesh: 'Malla Especial',
    specialMeshDesc: '¿Necesita una solución personalizada? Contáctenos para requerimientos especiales.',
    requestSpecialMesh: 'Solicitar Malla Especial',
    specialMeshFormTitle: 'Solicitud de Malla Especial',
    specialMeshFormSubtitle: 'Complete sus datos y adjunte su plano para obtener una cotización de malla personalizada.',
    yourName: 'Su Nombre',
    yourEmail: 'Su Email',
    company: 'Empresa',
    requirements: 'Requisitos / Especificaciones',
    attachDrawing: 'Adjuntar su plano',
    attachDrawingHint: 'Se aceptan archivos PDF, DWG o imágenes',
    fileSelected: 'Archivo seleccionado',
    removeFile: 'Eliminar',
    sendRequest: 'Enviar Solicitud',
    sending: 'Enviando...',
    thankYou: '¡Gracias!',
    requestReceived: 'Su solicitud ha sido recibida. Nuestro equipo se pondrá en contacto pronto.',
    close: 'Cerrar',
    tableHeaders: {
      reference: 'Referencia',
      diameter: 'Ø (mm)',
      dimensions: 'Dimensiones (mm)',
      trama: 'Trama (mm)',
      weightPiece: 'kg/pieza',
      piecesBundle: 'pzas/paq',
      weightBundle: 'kg/paq',
      download: 'Descargar',
    },
    downloadPdf: 'PDF',
    datasheetPending: 'Pendiente',
  },
  fr: {
    pageTitle: 'Treillis Soudé',
    pageSubtitle: 'Treillis d\'acier pour la construction',
    backToProducts: 'Retour aux Produits',
    selectCountry: 'Sélectionnez votre marché',
    viewFamilies: 'Voir les Familles',
    certification: 'Certificat',
    searchPlaceholder: 'Rechercher par référence (ex: Q257-A, ECO-1504...)',
    noResults: 'Aucune référence trouvée',
    clearSearch: 'Effacer la recherche',
    references: 'références',
    specialMesh: 'Treillis Spécial',
    specialMeshDesc: 'Besoin d\'une solution personnalisée? Contactez-nous.',
    requestSpecialMesh: 'Demander un Treillis Spécial',
    specialMeshFormTitle: 'Demande de Treillis Spécial',
    specialMeshFormSubtitle: 'Remplissez vos coordonnées et joignez votre plan.',
    yourName: 'Votre Nom',
    yourEmail: 'Votre Email',
    company: 'Entreprise',
    requirements: 'Exigences / Spécifications',
    attachDrawing: 'Joindre votre plan',
    attachDrawingHint: 'Fichiers PDF, DWG ou images acceptés',
    fileSelected: 'Fichier sélectionné',
    removeFile: 'Supprimer',
    sendRequest: 'Envoyer la Demande',
    sending: 'Envoi en cours...',
    thankYou: 'Merci !',
    requestReceived: 'Votre demande a été reçue. Notre équipe vous contactera bientôt.',
    close: 'Fermer',
    tableHeaders: {
      reference: 'Référence',
      diameter: 'Ø (mm)',
      dimensions: 'Dimensions (mm)',
      trama: 'Maille (mm)',
      weightPiece: 'kg/pièce',
      piecesBundle: 'pcs/paquet',
      weightBundle: 'kg/paquet',
      download: 'Télécharger',
    },
    downloadPdf: 'PDF',
    datasheetPending: 'En attente',
  },
  de: {
    pageTitle: 'Geschweißtes Gitter',
    pageSubtitle: 'Bewehrungsstahlgitter für den Bau',
    backToProducts: 'Zurück zu Produkten',
    selectCountry: 'Wählen Sie Ihren Markt',
    viewFamilies: 'Familien Anzeigen',
    certification: 'Zertifikat',
    searchPlaceholder: 'Suche nach Referenz (z.B. Q257-A, ECO-1504...)',
    noResults: 'Keine Referenzen gefunden',
    clearSearch: 'Suche löschen',
    references: 'Referenzen',
    specialMesh: 'Spezialgitter',
    specialMeshDesc: 'Benötigen Sie eine maßgeschneiderte Lösung? Kontaktieren Sie uns.',
    requestSpecialMesh: 'Spezialgitter anfordern',
    specialMeshFormTitle: 'Spezialgitter-Anfrage',
    specialMeshFormSubtitle: 'Geben Sie Ihre Daten ein und fügen Sie Ihre Zeichnung bei.',
    yourName: 'Ihr Name',
    yourEmail: 'Ihre E-Mail',
    company: 'Unternehmen',
    requirements: 'Anforderungen / Spezifikationen',
    attachDrawing: 'Zeichnung anhängen',
    attachDrawingHint: 'PDF-, DWG- oder Bilddateien akzeptiert',
    fileSelected: 'Datei ausgewählt',
    removeFile: 'Entfernen',
    sendRequest: 'Anfrage senden',
    sending: 'Wird gesendet...',
    thankYou: 'Danke!',
    requestReceived: 'Ihre Anfrage wurde empfangen. Unser Team wird sich in Kürze bei Ihnen melden.',
    close: 'Schließen',
    tableHeaders: {
      reference: 'Referenz',
      diameter: 'Ø (mm)',
      dimensions: 'Abmessungen (mm)',
      trama: 'Masche (mm)',
      weightPiece: 'kg/Stück',
      piecesBundle: 'Stk/Bund',
      weightBundle: 'kg/Bund',
      download: 'Download',
    },
    downloadPdf: 'PDF',
    datasheetPending: 'Ausstehend',
  },
  nl: {
    pageTitle: 'Gelast Gaas',
    pageSubtitle: 'Wapeningsstaal gaas voor de bouw',
    backToProducts: 'Terug naar Producten',
    selectCountry: 'Selecteer uw markt',
    viewFamilies: 'Bekijk Families',
    certification: 'Certificaat',
    searchPlaceholder: 'Zoeken op referentie (bijv. Q257-A, ECO-1504...)',
    noResults: 'Geen referenties gevonden',
    clearSearch: 'Zoekopdracht wissen',
    references: 'referenties',
    specialMesh: 'Speciaal Gaas',
    specialMeshDesc: 'Heeft u een oplossing op maat nodig? Neem contact met ons op.',
    requestSpecialMesh: 'Speciaal Gaas Aanvragen',
    specialMeshFormTitle: 'Aanvraag Speciaal Gaas',
    specialMeshFormSubtitle: 'Vul uw gegevens in en voeg uw tekening bij.',
    yourName: 'Uw Naam',
    yourEmail: 'Uw E-mail',
    company: 'Bedrijf',
    requirements: 'Vereisten / Specificaties',
    attachDrawing: 'Tekening bijvoegen',
    attachDrawingHint: 'PDF-, DWG- of afbeeldingsbestanden geaccepteerd',
    fileSelected: 'Bestand geselecteerd',
    removeFile: 'Verwijderen',
    sendRequest: 'Aanvraag Verzenden',
    sending: 'Verzenden...',
    thankYou: 'Dank u!',
    requestReceived: 'Uw aanvraag is ontvangen. Ons team zal binnenkort contact met u opnemen.',
    close: 'Sluiten',
    tableHeaders: {
      reference: 'Referentie',
      diameter: 'Ø (mm)',
      dimensions: 'Afmetingen (mm)',
      trama: 'Maas (mm)',
      weightPiece: 'kg/stuk',
      piecesBundle: 'stuks/bundel',
      weightBundle: 'kg/bundel',
      download: 'Download',
    },
    downloadPdf: 'PDF',
    datasheetPending: 'In behandeling',
  }
};

// ============== COMPONENTS ==============

// Country Block Component (B2B industrial style with map silhouette)
const CountryBlock = ({ country, isSelected, onClick, t, language }) => {
  const countryName = country.name[language] || country.name.en;
  
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between p-4 border-2 transition-all ${
        isSelected 
          ? 'border-green-600 bg-green-50' 
          : 'border-slate-200 bg-white hover:border-green-400 hover:bg-slate-50'
      }`}
      data-testid={`country-block-${country.code}`}
    >
      <div className="flex items-center space-x-4">
        {/* Country Map Silhouette */}
        <div className="w-16 h-12 bg-slate-100 rounded overflow-hidden flex items-center justify-center">
          <img 
            src={country.mapImage} 
            alt={countryName}
            className="w-full h-full object-contain p-1"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = document.createElement('span');
              fallback.className = 'text-2xl font-bold text-slate-400';
              fallback.textContent = country.code;
              e.target.parentElement.appendChild(fallback);
            }}
          />
        </div>
        <div className="text-left">
          <h3 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight">
            {countryName}
          </h3>
          <div className="flex items-center space-x-2 mt-1">
            <Award className="h-4 w-4 text-green-600" />
            <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5">
              {t.certification}: {country.certification}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-2 text-sm font-medium text-slate-600">
        <span className="hidden sm:inline">{t.viewFamilies}</span>
        <ChevronRight className={`h-5 w-5 transition-transform ${isSelected ? 'rotate-90 text-green-600' : 'text-slate-400'}`} />
      </div>
    </button>
  );
};

// Family Accordion with Table
const MeshFamilyAccordion = ({ family, t, language, isExpanded, onToggle, searchQuery }) => {
  const filteredProducts = family.products.filter(p => {
    return !searchQuery || p.ref.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (filteredProducts.length === 0) return null;

  return (
    <div className="border border-slate-200 bg-white mb-2" data-testid={`mesh-family-${family.name.toLowerCase().replace(/\s/g, '-')}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <span className="font-heading font-bold text-slate-900 uppercase tracking-tight">{family.name}</span>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5">
            {filteredProducts.length} {t.references}
          </span>
        </div>
        {isExpanded ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
      </button>
      
      {isExpanded && (
        <div className="border-t border-slate-200 overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-slate-900 text-white text-xs uppercase tracking-wider">
                <th className="p-3 text-left font-medium">{t.tableHeaders.reference}</th>
                <th className="p-3 text-center font-medium">{t.tableHeaders.diameter}</th>
                <th className="p-3 text-center font-medium">{t.tableHeaders.dimensions}</th>
                <th className="p-3 text-center font-medium">{t.tableHeaders.trama}</th>
                <th className="p-3 text-center font-medium">{t.tableHeaders.weightPiece}</th>
                <th className="p-3 text-center font-medium">{t.tableHeaders.piecesBundle}</th>
                <th className="p-3 text-center font-medium">{t.tableHeaders.weightBundle}</th>
                <th className="p-3 text-center font-medium">{t.tableHeaders.download}</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product, idx) => (
                <tr 
                  key={product.ref} 
                  className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-green-50 transition-colors ${searchQuery && product.ref.toLowerCase().includes(searchQuery.toLowerCase()) ? 'bg-green-50' : ''}`}
                >
                  <td className="p-3 font-mono font-bold text-slate-900">{product.ref}</td>
                  <td className="p-3 text-center font-mono text-sm text-slate-700">{product.diameter}</td>
                  <td className="p-3 text-center font-mono text-sm text-slate-700">{product.dimensions}</td>
                  <td className="p-3 text-center font-mono text-sm text-slate-700">{product.trama}</td>
                  <td className="p-3 text-center font-mono text-sm text-slate-700">{product.weightPiece?.toFixed(2) || '-'}</td>
                  <td className="p-3 text-center font-mono text-sm text-slate-700">{product.piecesBundle || '-'}</td>
                  <td className="p-3 text-center font-mono text-sm text-slate-700">{product.weightBundle || '-'}</td>
                  <td className="p-3 text-center">
                    {product.datasheet ? (
                      <a
                        href={product.datasheet}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-1 text-green-600 hover:text-green-700 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        <span className="text-xs font-medium">{t.downloadPdf}</span>
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400 italic">{t.datasheetPending}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============== MAIN PAGE COMPONENT ==============

export const WeldedMesh = () => {
  const { language } = useLanguage();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const searchInputRef = useRef(null);
  
  // Special Mesh Modal State
  const [showSpecialMeshModal, setShowSpecialMeshModal] = useState(false);
  const [specialMeshForm, setSpecialMeshForm] = useState({
    name: '',
    email: '',
    company: '',
    country: '',
    requirements: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const fileInputRef = useRef(null);

  const t = translations[language] || translations.en;

  // Get families for selected country
  const getFamiliesForCountry = (countryCode) => {
    const config = COUNTRY_CONFIG[countryCode];
    if (!config) return [];
    
    if (config.plant === 'sevilla') {
      return MESH_FAMILIES_SEVILLA.filter(f => f.markets.includes(countryCode));
    } else {
      return MESH_FAMILIES_LIEGE.filter(f => f.markets.includes(countryCode));
    }
  };

  const toggleFamily = (familyKey) => {
    setExpandedFamilies(prev => ({ ...prev, [familyKey]: !prev[familyKey] }));
  };

  // Auto-expand families when search matches
  useEffect(() => {
    if (searchQuery && selectedCountry) {
      const newExpanded = {};
      const families = getFamiliesForCountry(selectedCountry);
      families.forEach(family => {
        if (family.products.some(p => p.ref.toLowerCase().includes(searchQuery.toLowerCase()))) {
          newExpanded[family.name] = true;
        }
      });
      setExpandedFamilies(newExpanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedCountry]);

  const clearSearch = () => {
    setSearchQuery('');
    setExpandedFamilies({});
    searchInputRef.current?.focus();
  };

  // Check if there are any results
  const hasAnyResults = React.useMemo(() => {
    if (!searchQuery || !selectedCountry) return true;
    const families = getFamiliesForCountry(selectedCountry);
    return families.some(f => f.products.some(p => p.ref.toLowerCase().includes(searchQuery.toLowerCase())));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedCountry]);

  // Special Mesh Form Handlers
  const openSpecialMeshModal = () => {
    setShowSpecialMeshModal(true);
    setFormSuccess(false);
    setFormError('');
    setSpecialMeshForm({
      name: '',
      email: '',
      company: '',
      country: selectedCountry || '',
      requirements: ''
    });
    setSelectedFile(null);
  };

  const closeSpecialMeshModal = () => {
    setShowSpecialMeshModal(false);
    setFormSuccess(false);
    setFormError('');
    setSelectedFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setFormError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setFormError('');
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSpecialMeshSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError('');

    try {
      let fileData = null;
      let fileName = null;
      let fileType = null;

      if (selectedFile) {
        fileName = selectedFile.name;
        fileType = selectedFile.type;
        const reader = new FileReader();
        fileData = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(selectedFile);
        });
      }

      await axios.post(`${API}/special-mesh-request`, {
        name: specialMeshForm.name,
        email: specialMeshForm.email,
        company: specialMeshForm.company,
        country: specialMeshForm.country || 'Not specified',
        requirements: specialMeshForm.requirements,
        language: language,
        file_name: fileName,
        file_data: fileData,
        file_type: fileType
      });

      setFormSuccess(true);
    } catch (error) {
      setFormError('Error sending request. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 pt-16 md:pt-20" data-testid="welded-mesh-page">
      {/* Hero */}
      <section className="relative py-16 md:py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="inline-flex items-center space-x-2 text-green-500 hover:text-green-400 mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">{t.backToProducts}</span>
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-4">{t.pageTitle}</h1>
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl">{t.pageSubtitle}</p>
        </div>
      </section>

      <div className="bg-slate-50">
      {/* Country Selection */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight mb-4">
            {t.selectCountry}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {COUNTRY_ORDER.map((code) => (
              <CountryBlock
                key={code}
                country={COUNTRY_CONFIG[code]}
                isSelected={selectedCountry === code}
                onClick={() => setSelectedCountry(selectedCountry === code ? null : code)}
                t={t}
                language={language}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Search Bar (only visible when country selected) */}
      {selectedCountry && (
        <section className="py-4 bg-white border-b border-slate-200 sticky top-16 md:top-20 z-[45]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full pl-10 pr-10 py-3 border border-slate-300 bg-white text-sm focus:border-green-600 focus:ring-1 focus:ring-green-600 outline-none"
                data-testid="reference-search"
              />
              {searchQuery && (
                <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Main Content - Product Families */}
      {selectedCountry && (
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Country Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-slate-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-8 bg-slate-100 rounded overflow-hidden flex items-center justify-center">
                  <img 
                    src={COUNTRY_CONFIG[selectedCountry].mapImage} 
                    alt={COUNTRY_CONFIG[selectedCountry].name[language]}
                    className="w-full h-full object-contain p-1"
                  />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight">
                    {COUNTRY_CONFIG[selectedCountry].name[language]}
                  </h2>
                  <div className="flex items-center space-x-2 mt-1">
                    <Award className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-bold text-green-700">
                      {t.certification}: {COUNTRY_CONFIG[selectedCountry].certification}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {!hasAnyResults ? (
              <div className="text-center py-16">
                <p className="text-slate-500 text-lg">{t.noResults}</p>
                <button onClick={clearSearch} className="mt-4 text-green-600 hover:text-green-700 font-medium">{t.clearSearch}</button>
              </div>
            ) : (
              <>
                <div className="space-y-0">
                  {getFamiliesForCountry(selectedCountry).map((family) => (
                    <MeshFamilyAccordion
                      key={family.name}
                      family={family}
                      t={t}
                      language={language}
                      isExpanded={expandedFamilies[family.name]}
                      onToggle={() => toggleFamily(family.name)}
                      searchQuery={searchQuery}
                    />
                  ))}
                </div>

                {/* Special Mesh CTA */}
                <div className="bg-slate-100 border border-slate-200 p-8 text-center mt-8">
                  <MessageSquare className="h-10 w-10 text-green-600 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-2">{t.specialMesh}</h3>
                  <p className="text-slate-600 mb-6 max-w-lg mx-auto">{t.specialMeshDesc}</p>
                  <button 
                    onClick={openSpecialMeshModal}
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-green-600 text-white font-bold uppercase tracking-wider hover:bg-green-700 transition-colors"
                    data-testid="special-mesh-cta"
                  >
                    <span>{t.requestSpecialMesh}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Prompt to select country when none selected */}
      {!selectedCountry && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Award className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">{t.selectCountry}</p>
          </div>
        </section>
      )}

      </div>

      {/* Special Mesh Request Modal */}
      {showSpecialMeshModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={closeSpecialMeshModal}
          data-testid="special-mesh-modal"
        >
          <div
            className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeSpecialMeshModal}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10"
              data-testid="modal-close-btn"
            >
              <X className="h-6 w-6" />
            </button>

            <div className="p-6 border-b border-slate-200">
              <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight pr-8">
                {t.specialMeshFormTitle}
              </h2>
              <p className="text-slate-600 text-sm mt-2">
                {t.specialMeshFormSubtitle}
              </p>
            </div>

            <div className="p-6">
              {formSuccess ? (
                <div className="text-center py-8" data-testid="form-success-message">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">{t.thankYou}</h3>
                  <p className="text-slate-600">{t.requestReceived}</p>
                  <Button
                    onClick={closeSpecialMeshModal}
                    className="mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-none uppercase tracking-wider font-bold"
                    data-testid="close-success-btn"
                  >
                    {t.close}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSpecialMeshSubmit} className="space-y-4">
                  {formError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="form-error">
                      {formError}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t.yourName} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={specialMeshForm.name}
                      onChange={(e) => setSpecialMeshForm({ ...specialMeshForm, name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-colors"
                      data-testid="special-mesh-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t.company} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={specialMeshForm.company}
                      onChange={(e) => setSpecialMeshForm({ ...specialMeshForm, company: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-colors"
                      data-testid="special-mesh-company"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t.yourEmail} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={specialMeshForm.email}
                      onChange={(e) => setSpecialMeshForm({ ...specialMeshForm, email: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-colors"
                      data-testid="special-mesh-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t.requirements} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={specialMeshForm.requirements}
                      onChange={(e) => setSpecialMeshForm({ ...specialMeshForm, requirements: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-colors resize-none"
                      data-testid="special-mesh-requirements"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t.attachDrawing}
                    </label>
                    
                    {selectedFile ? (
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-5 w-5 text-green-600" />
                          <span className="text-sm text-green-800 font-medium truncate max-w-[200px]">
                            {selectedFile.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={removeSelectedFile}
                          className="text-red-500 hover:text-red-700 text-sm font-medium"
                          data-testid="remove-file-btn"
                        >
                          {t.removeFile}
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-slate-300 p-6 text-center cursor-pointer hover:border-green-500 hover:bg-green-50/50 transition-colors"
                        data-testid="file-upload-area"
                      >
                        <Paperclip className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-slate-700">{t.attachDrawing}</p>
                        <p className="text-xs text-slate-500 mt-1">{t.attachDrawingHint}</p>
                      </div>
                    )}
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.dwg,.dxf,.png,.jpg,.jpeg,.gif"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full bg-green-600 hover:bg-green-700 text-white rounded-none uppercase tracking-wider font-bold py-3"
                    data-testid="submit-special-mesh-btn"
                  >
                    {formSubmitting ? t.sending : t.sendRequest}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
